// Libraries
import { useState, useCallback } from 'react';
import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import cn from 'classnames';
import { Grid } from 'ht-styleguide';

/* Types & Utils */
import { TClientPlans } from 'types/plans.types';
import useCartMutationSelectplan from '@store/queries/cart/useCartMutation.selectplan';
import { isMobileOrTablet } from '@utils/ui';
import { cartPath } from '@constants/paths';
import { formatPlansForReducer, getPlansReviews, getAnnualSavings, formatHTLitePlanForReducer } from '@utils/plans';

/* Queries */
import useCartMutationStartBooking, { handleSingleServiceBookingSuccess } from '@store/queries/cart/useCartMutation.startbooking';

/* Components */
import HeaderBlock from './Parts/HeaderBlock';
import SelectPlanBanner from './Parts/PricingTable/PricingCarousel/SelectPlanBanner';
import PricingCarousel from './Parts/PricingTable/PricingCarousel';
import PricingTable from './Parts/PricingTable/Table';
import LazyComponent from '@components/LazyComponent';
import Maybe from '@components/Maybe';

/* Hooks */
import { useIsPasswordlessCheckoutEnabled } from '@scenes/LoginPage/Modal/LoginPasscode/LoginPasscode.hooks';
import { useCartFlow } from 'src/utils/cart/useCartFlow';

/* Constants & Styles */
import { UPSELL_PLAN_BILLING_CYCLES } from 'src/constants/plan';
import { GUARANTEE_TEXTS, HEADER_TEXTS, META_DESCRIPTION, META_TITLE } from './planspage.constants';
import styles from './plansPage.module.scss';

/* Dynamic imports */
const PlanGuaranteeBlockDynamic = dynamic(() => import('./Parts/PlanGuaranteeBlock'), { ssr: false });
const TechInfoBlockDynamic = dynamic(() => import('./Parts/PlanInfoBlockTechInfo'), { ssr: false });
const HardwareInfoBlockDynamic = dynamic(() => import('./Parts/PlanInfoBlockHardwareInfo'), { ssr: false });
const EverythingYouGetBlockDynamic = dynamic(() => import('./Parts/EverythingYouGetBlock'), { ssr: false });
const ChoosePlanBlockDynamic = dynamic(() => import('./Parts/ChoosePlanBlock'), { ssr: false });
const PlanFaqDynamic = dynamic(() => import('./Parts/PlanFaq'), { ssr: false });
const ChatBoxBlockDynamic = dynamic(() => import('@components/ChatWithAgentBlock'), { ssr: false });
const CustomerReviewsDynamic = dynamic(() => import('@components/UI/Reviews'), { ssr: false });

type PlansPageProps = {
  plans: TClientPlans[];
};

const PlansPage = ({ plans }: PlansPageProps) => {
  /* Hooks */
  const [billingCycle, setBillingCycle] = useState(UPSELL_PLAN_BILLING_CYCLES.ANNUAL);
  const isPasswordlessCheckoutEnabled = useIsPasswordlessCheckoutEnabled();
  const { isSingleServiceFlow } = useCartFlow();
  const router = useRouter();

  /* Mutations */
  const { mutate } = useCartMutationSelectplan();
  const startBookingMutation = useCartMutationStartBooking();

  /* Constants */
  const planName = plans[0].name;
  const reviews = getPlansReviews({ planProducts: plans });
  const smartHome = formatPlansForReducer({
    planProducts: plans,
    featured: true,
    isMobileOrTablet: isMobileOrTablet(),
  });
  const annualSavings = getAnnualSavings(smartHome) || '';
  const secondary = formatHTLitePlanForReducer({ planProducts: plans });
  const plansInfo = { smartHome, secondary };

  /** Save the result to a variable so that we can add to the deps array */
  const isSingleServiceCart = isSingleServiceFlow();
  const addToCart = useCallback(
    ({ planId }: { planId: number }) => {
      mutate(
        { planId },
        {
          onSuccess: () => {
            /* 1. If single service, start booking */
            if (isSingleServiceCart) {
              /* 1. Mutate: start booking */
              startBookingMutation.mutate(undefined, {
                onSuccess: () => {
                  handleSingleServiceBookingSuccess({ router, isPasswordless: isPasswordlessCheckoutEnabled });
                },
              });
            } else {
              // Infinite/multi-service cart
              window.location.href = cartPath();
            }
          },
        },
      );
    },
    [isSingleServiceCart, isPasswordlessCheckoutEnabled, mutate, router, startBookingMutation],
  );

  return (
    <>
      <Head>{generateNextSeo({ title: META_TITLE, description: META_DESCRIPTION })}</Head>
      <HeaderBlock title={HEADER_TEXTS.title} subTitle={HEADER_TEXTS.subTitle}>
        <PricingTable
          containerClass={cn('marginBottom-huge1', styles.desktopPricingInfo)}
          plansInfo={plansInfo}
          annualSavings={annualSavings}
          billingCycle={billingCycle}
          setBillingCycle={setBillingCycle}
          addToCart={addToCart}
        />
      </HeaderBlock>
      <Grid.FullWidth classes={styles.mobilePricingInfo}>
        <SelectPlanBanner
          containerClass={styles.selectPlanBanner}
          annualSavings={annualSavings}
          billingCycle={billingCycle}
          setBillingCycle={setBillingCycle}
        />
        <PricingCarousel plansInfo={plansInfo} addToCart={addToCart} billingCycle={billingCycle} />
      </Grid.FullWidth>
      <LazyComponent minHeight="100px">
        <PlanGuaranteeBlockDynamic title={GUARANTEE_TEXTS.title} text={GUARANTEE_TEXTS.subTitle} />
      </LazyComponent>
      <LazyComponent minHeight="400px">
        <TechInfoBlockDynamic />
      </LazyComponent>
      <LazyComponent minHeight="400px">
        <HardwareInfoBlockDynamic />
      </LazyComponent>
      <LazyComponent minHeight="500px">
        <EverythingYouGetBlockDynamic />
      </LazyComponent>
      <LazyComponent minHeight="300px">
        <ChoosePlanBlockDynamic />
      </LazyComponent>
      <Maybe condition={Array.isArray(reviews)}>
        <LazyComponent minHeight="200px">
          <CustomerReviewsDynamic
            reviews={reviews}
            className={cn(styles.reviews, 'paddingY-huge1')}
            carouselItemStyle={styles.reviewItem}
            headerStyles="text-align-center"
            headerText="What our members say"
          />
        </LazyComponent>
      </Maybe>
      <LazyComponent minHeight="400px">
        <PlanFaqDynamic planName={planName} />
      </LazyComponent>
      <LazyComponent minHeight="300px">
        <ChatBoxBlockDynamic />
      </LazyComponent>
    </>
  );
};

export default PlansPage;
